import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { NgClass, AsyncPipe, TitleCasePipe, UpperCasePipe } from '@angular/common';
import { Component, OnInit, HostListener, Renderer2 } from '@angular/core';
import { RequestService } from 'src/app/core/request/request.service';
import { AuthService } from 'src/app/core';
import { Partner } from 'src/app/config/partner/_default';
import { SidebarToggleService } from '../navigation-sidebar/sidebar-toggle.service';
import { CompanyService, CompanyDto } from 'src/app/core/customer/customer-client/company.service';
import { Subscription } from 'rxjs';
import { NewUserService } from 'src/app/core/customer/customer-client/new-user.service';
import { RbacDirective } from 'src/app/shared/directives';
import { RbacPipe } from 'src/app/shared/pipes/rbac.pipe';
import { CompanySelectorComponent } from '../company-selector/company-selector.component';
import { VersionSwitchComponent } from '../version-switch/version-switch.component';

@Component({
    standalone: true,
    selector: 'app-navigation-main',
    templateUrl: './navigation-main.component.html',
    styleUrls: ['./navigation-main.component.scss'],
    imports: [AsyncPipe, CompanySelectorComponent, NgClass, RbacDirective, RbacPipe, RouterLink, RouterLinkActive, TitleCasePipe, TranslatePipe, UpperCasePipe, VersionSwitchComponent],
})
export class NavigationMainComponent implements OnInit {

  public selectedCompanyDisplay = "";
  public selectedCompanyGuid?: string;

  readonly Partner = Partner;
  public companyName: string;
  public showNavbar = false;
  public showUsermenu = false;
  public languages: string[];
  public checkPermission = false;
  public isMenuCollapsed = true;
  public isSmallScreen = false;
  public unreadCount = -1;

  public currentUrl = "";
  public jobServiceUrlWithToken = Partner.jobServiceUrl;

  private changeSelectedCompanySubscription?: Subscription;

  constructor(
    public requests: RequestService,
    public translate: TranslateService,
    private authService: AuthService,
    private companyService: CompanyService,
    private sidebar: SidebarToggleService,
    public router: Router,
    private renderer: Renderer2,
    private userService:  NewUserService
  ) {

  }

  public getCurrentHelpRoute(): string {
    const routeParts = this.router.url.split(/[/\?]/);
    return routeParts.slice(0, 2).join("/") + "/help";
  }

  toggle() {
    this.isMenuCollapsed = !this.isMenuCollapsed;
    this.sidebar.toggle()
  }

  ngOnInit() {
    this.isMenuCollapsed = this.sidebar.isMenuCollapsed;
    this.isSmallScreen = window.innerWidth <= 1850; // Change the breakpoint as needed
    this.currentUrl = window.location.href;
    this.jobServiceUrlWithToken = this.buildJobServiceUrlWithToken();

    this.getLanguages();
    this.translate.onLangChange.subscribe(() => this.getLanguages());

    //documentsigner sign page does not require any user so skip subscribeUser
    if(!this.username && this.currentUrl.includes("/sign?token"))
      return;

    this.authService.subscribeUser().subscribe(x => {
      if (this.authService.User) {
        this.selectedCompanyGuid = this.authService.User.SelectedCompanyUid;
        if (this.authService.User.Roles)
          this.CheckUserRole(this.authService.User.Roles);
        if (this.selectedCompanyGuid) {
          this.companyService.get(this.selectedCompanyGuid).subscribe(x => {
            if (x.searchName === null || x.searchName === undefined || x.searchName === "") {
              this.selectedCompanyDisplay = x.datiAnagrafici.anagrafica.nome + " " + x.datiAnagrafici.anagrafica.cognome;
            } else {
              this.selectedCompanyDisplay = x.searchName;
            }
          });
        }
      }
    });

    this.loadChangelog();
  }

  public toggleNavbar(shown: boolean | null = null) {
    if (shown !== null) {
      this.showNavbar = shown;
    } else {
      this.showNavbar = !this.showNavbar;
      if (this.showUsermenu) {
        this.toggleUsermenu(false);
      }
    }
  }

  public toggleUsermenu(shown: boolean | null = null) {
    if (shown !== null) {
      this.showUsermenu = shown;
    } else {
      this.showUsermenu = !this.showUsermenu;
      if (this.showNavbar) {
        this.toggleNavbar(false);
      }
    }
  }

  public getLanguages(): void {
    this.languages = this.translate.getLangs().filter((lang: string) => lang !== this.translate.currentLang);
  }

  public changeLanguage(locale: string): void {
    this.translate.use(locale);

    localStorage.setItem('locale', locale);
    this.toggleUsermenu(false);
  }

  public logout(): void {
    this.authService.logout();
  }

  public changeCompany(company?: CompanyDto) {
    this.authService.User!.SelectedCompanyUid = company?.guid;
    this.authService.User!.SelectedCompany = company;
    this.selectedCompanyGuid = company?.guid;
    if (company) {      
      this.selectedCompanyDisplay = company.searchName;
    }
    else {
      this.selectedCompanyDisplay = "";
      this.selectedCompanyDisplay = "";
    }

    this.changeSelectedCompanySubscription = this.userService.updateSelectedCompany(this.selectedCompanyGuid!).subscribe({
      complete: async () => {
        await this.authService.refreshToken();
        this.changeSelectedCompanySubscription?.unsubscribe();
        this.changeSelectedCompanySubscription = undefined;
      }
    });  
  }

  private CheckUserRole(rolearr: string[]): void {
    //check userrole area2 exists in the user role
    for (let i = 0; i < rolearr.length; i++) {
      if (rolearr[i] == "area2") {
        this.checkPermission = true
      }
    }
  }

  public get username(): string {
    return this.authService.User?.FullName!;
  }

  private buildJobServiceUrlWithToken(): string {
    const baseUrl = this.Partner.jobServiceUrl;
    const rawToken = this.authService.authenticationToken ?? "";
    const jwtToken = typeof rawToken === "string" ? rawToken.replace(/^Bearer\s+/i, "") : "";

    if (!baseUrl || !jwtToken) {
      return baseUrl;
    }

    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}token=${encodeURIComponent(jwtToken)}`;
  }

  // Add this HostListener function to listen for window resize events
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.isSmallScreen = window.innerWidth <= 1850; // Change the breakpoint as needed
  }

  private featurebaseConflictResolveAttempted = false;
  private readonly featurebaseOrganization = "systems";

  loadChangelog() {
    this.userService.getFeatureBaseToken().subscribe(token => {
      this.loadFeaturebaseSDK()
      .then(() => this.checkFeaturebaseAccess(token))
      .then(hasAccess => {
        if (!hasAccess)
          return;

        const win: any = window;
        if (typeof win.Featurebase !== "function") {
          win.Featurebase = function () {
            (win.Featurebase.q = win.Featurebase.q || []).push(arguments);
          };
        }

        win.Featurebase("init_changelog_widget", {
          organization: this.featurebaseOrganization,
          jwtToken: token,
          popup: {
            enabled: true,
            usersName: this.username,
            autoOpenForNewUpdates: true
          },
          category: ["s-Invoice","s-Order","s-Archive","s-Signer","s-Banking"],
          theme: "light",
          locale: this.translate.currentLang,
        },
          (error: any, data: any) => {
            if (data?.action === 'unreadChangelogsCountChanged')
              this.unreadCount = data.unreadCount;
          }
        );
      })
      .catch(error => {
        console.log("Error initializing Featurebase", error);
      });
    });
  }

  // The Featurebase SDK swallows widget-config failures internally (it only logs a
  // console.warn and never passes errors to the init callback), so the config endpoint
  // has to be probed directly to detect and resolve the user id conflict.
  private async checkFeaturebaseAccess(token: string | null): Promise<boolean> {
    const response = await fetch(`https://${this.featurebaseOrganization}.featurebase.app/api/v1/changelog/widget/config`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ jwtToken: token, locale: this.translate.currentLang })
    });

    if (response.ok)
      return true;

    const body = await response.text().catch(() => '');
    if (response.status === 500 || this.isFeaturebaseUserIdConflict(body))
      this.resolveFeaturebaseConflict();

    return false;
  }

  private isFeaturebaseUserIdConflict(error: any): boolean {
    const message = typeof error === 'string' ? error : JSON.stringify(error);
    return !!message?.toLowerCase().includes('user id already taken');
  }

  private resolveFeaturebaseConflict() {
    if (this.featurebaseConflictResolveAttempted)
      return;

    this.featurebaseConflictResolveAttempted = true;

    this.userService.resolveFeatureBaseConflict().subscribe(deleted => {
      if (deleted)
        this.loadChangelog();
    });
  }

  private loadFeaturebaseSDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if the script is already present
      if (document.getElementById('featurebase-sdk')) {
        resolve();
        return;
      }
      const script = this.renderer.createElement('script');
      script.id = 'featurebase-sdk';
      script.src = 'https://do.featurebase.app/js/sdk.js';
      script.onload = () => resolve();
      script.onerror = (error: any) => reject(error);
      this.renderer.appendChild(document.body, script);
    });
  }

  openChangelogWidget() {
    const win: any = window;
    
    if (typeof win.Featurebase !== "function")
      return;

    win.Featurebase('manually_open_changelog_popup');
  }

  public requestDocumentDownload(): void {
    document.dispatchEvent(new CustomEvent('documentSignerDownload', { bubbles: true }));
  }
}
